/**
 * Thread Transformer Utility
 * Transforms API responses to match UI data structures
 */

/**
 * Transform API thread list item to sidebar format
 * @param {Object} apiThread - Thread from GET /threads
 * @returns {Object} Thread in UI format
 */
export const transformThreadForSidebar = (apiThread) => {
  return {
    id: apiThread.threadId,
    threadId: apiThread.threadId,
    date: apiThread.dateCreated,
    title: apiThread.title || apiThread.firstMessage || 'New Chat',
    messageCount: apiThread.messageCount || 0,
    lastActivity: apiThread.lastActivity,
    status: apiThread.status,
    isLiked: apiThread.isLiked || false,
  };
};

/**
 * Transform array of API threads to sidebar format
 * @param {Array} apiThreads - Threads from GET /threads
 * @returns {Array} Threads in UI format
 */
export const transformThreadsForSidebar = (apiThreads) => {
  if (!apiThreads || !Array.isArray(apiThreads)) {
    return [];
  }
  return apiThreads.map(transformThreadForSidebar);
};

/**
 * Transform API thread detail to chat messages format
 * Pairs user/assistant messages into the format ChatContainer expects
 * Handles both single assistant responses and SQL + interpretation pairs
 * @param {Object} apiThread - Thread from GET /threads/{threadId}
 * @returns {Array} Array of chat messages for ChatContainer
 */
export const transformThreadToMessages = (apiThread) => {
  if (!apiThread || !apiThread.messages || !Array.isArray(apiThread.messages)) {
    return [];
  }

  const messages = [];
  const sortedMessages = [...apiThread.messages].sort((a, b) => a.sortOrder - b.sortOrder);

  for (let i = 0; i < sortedMessages.length; i++) {
    const msg = sortedMessages[i];

    if (msg.role === 'user') {
      // Add user question
      messages.push({
        type: 'question',
        content: msg.content,
      });

      // Collect ALL consecutive assistant messages after this user message
      const assistantMessages = [];
      let j = i + 1;
      while (j < sortedMessages.length && sortedMessages[j].role === 'assistant') {
        assistantMessages.push(sortedMessages[j]);
        j++;
      }

      if (assistantMessages.length > 0) {
        // Build combined response from all assistant messages
        messages.push({
          type: 'bot',
          content: assistantMessages[0].content || '',
          rawResponse: buildCombinedRawResponse(assistantMessages),
          sql: assistantMessages[0].sqlQuery,
          isHistoryUpdated: true, // Mark as already in history
        });
        i = j - 1; // Skip all assistant messages
      }
    } else if (msg.role === 'assistant' && i === 0) {
      // Handle edge case where assistant message comes first (shouldn't normally happen)
      messages.push({
        type: 'bot',
        content: msg.content || '',
        rawResponse: buildRawResponse(msg),
        sql: msg.sqlQuery,
        isHistoryUpdated: true,
      });
    }
  }

  return messages;
};

/**
 * Build the rawResponse object from an assistant message
 * Matches the structure expected by ChatMessage component
 * @param {Object} assistantMsg - Assistant message from API
 * @returns {Object} Raw response for ChatMessage
 */
const buildRawResponse = (assistantMsg) => {
  return {
    message: assistantMsg.content,
    sql: assistantMsg.sqlQuery,
    results: assistantMsg.sqlResults,
    explanation: assistantMsg.explanation,
    elapsedTime: assistantMsg.elapsedTimeMs
      ? `${assistantMsg.elapsedTimeMs}ms`
      : null,
    error: assistantMsg.error,
  };
};

/**
 * Parse interpretation JSON from markdown code block or raw JSON string
 * @param {string} content - Raw content that may contain JSON
 * @returns {Object|null} Parsed interpretation object or null
 */
const parseInterpretation = (content) => {
  if (!content) return null;

  try {
    // Try to extract JSON from markdown code block (```json ... ```)
    const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)```/);
    const jsonStr = jsonMatch ? jsonMatch[1].trim() : content.trim();

    // Parse the JSON
    const parsed = JSON.parse(jsonStr);
    return parsed;
  } catch (e) {
    // If parsing fails, return null
    return null;
  }
};

/**
 * Build combined rawResponse from multiple assistant messages
 * Typically combines SQL response (first) with interpretation response (second)
 * @param {Array} assistantMessages - Array of assistant messages from API
 * @returns {Object} Combined raw response for ChatMessage
 */
const buildCombinedRawResponse = (assistantMessages) => {
  if (!assistantMessages || assistantMessages.length === 0) {
    return {};
  }

  // Start with the first message (typically has SQL query and results)
  const sqlMsg = assistantMessages[0];
  const response = {
    message: sqlMsg.content,
    sql: sqlMsg.sqlQuery,
    results: sqlMsg.sqlResults,
    explanation: sqlMsg.explanation,
    elapsedTime: sqlMsg.elapsedTimeMs
      ? `${sqlMsg.elapsedTimeMs}ms`
      : null,
    error: sqlMsg.error,
  };

  // If there's a second message (interpretation), merge its data
  if (assistantMessages.length > 1) {
    const interpretMsg = assistantMessages[1];

    // The interpretation message has the explanation with insights, map config, etc.
    // It's stored in both 'explanation' and 'content' fields as JSON wrapped in markdown
    const interpretationContent = interpretMsg.explanation || interpretMsg.content;

    if (interpretationContent) {
      response.explanation = interpretationContent;

      // Parse the interpretation JSON and set it as the interpretation object
      // This matches the format used when a fresh query is made
      const parsedInterpretation = parseInterpretation(interpretationContent);
      if (parsedInterpretation) {
        response.interpretation = parsedInterpretation;
      }
    }

    // Add elapsed time from interpretation if not already set
    if (!response.elapsedTime && interpretMsg.elapsedTimeMs) {
      response.elapsedTime = `${interpretMsg.elapsedTimeMs}ms`;
    }
  }

  return response;
};

/**
 * Group threads by date (today, yesterday, last 7 days, older)
 * @param {Array} threads - Threads in UI format (with date field)
 * @returns {Object} Grouped threads { today: [], yesterday: [], previousWeek: [], older: [] }
 */
export const groupThreadsByDate = (threads) => {
  if (!threads || !Array.isArray(threads)) {
    return { today: [], yesterday: [], previousWeek: [], older: [] };
  }

  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);

  return threads.reduce(
    (groups, thread) => {
      const threadDate = new Date(thread.date);
      threadDate.setHours(0, 0, 0, 0);

      // Handle future dates as today (for testing/demo data)
      if (threadDate > now || threadDate.getTime() === today.getTime()) {
        groups.today.push(thread);
      } else if (threadDate.getTime() === yesterday.getTime()) {
        groups.yesterday.push(thread);
      } else if (threadDate >= lastWeek) {
        groups.previousWeek.push(thread);
      } else {
        groups.older.push(thread);
      }
      return groups;
    },
    { today: [], yesterday: [], previousWeek: [], older: [] }
  );
};

/**
 * Transform API thread detail to history format (for compatibility)
 * @param {Object} apiThread - Thread from GET /threads/{threadId}
 * @returns {Object} History object matching old historyService format
 */
export const transformThreadToHistory = (apiThread) => {
  if (!apiThread) return null;

  const messages = [];
  const sortedMessages = [...(apiThread.messages || [])].sort((a, b) => a.sortOrder - b.sortOrder);

  // Pair user/assistant messages
  for (let i = 0; i < sortedMessages.length; i++) {
    const msg = sortedMessages[i];

    if (msg.role === 'user') {
      const assistantMsg = sortedMessages[i + 1];

      if (assistantMsg && assistantMsg.role === 'assistant') {
        messages.push({
          timestamp: msg.dateCreated,
          question: msg.content,
          answer: assistantMsg.content || '',
          apiResponse: {
            sql: assistantMsg.sqlQuery,
            results: assistantMsg.sqlResults,
            explanation: assistantMsg.explanation,
            elapsedTime: assistantMsg.elapsedTimeMs
              ? `${assistantMsg.elapsedTimeMs}ms`
              : null,
            error: assistantMsg.error,
          },
        });
        i++; // Skip assistant message
      }
    }
  }

  return {
    id: apiThread.threadId,
    threadId: apiThread.threadId,
    date: apiThread.dateCreated,
    title: apiThread.title || 'New Chat',
    messages,
    isLiked: apiThread.isLiked || false,
  };
};

export default {
  transformThreadForSidebar,
  transformThreadsForSidebar,
  transformThreadToMessages,
  groupThreadsByDate,
  transformThreadToHistory,
};
